import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { MaskedValue } from '@/components/atoms/MaskedValue';
import { useInvoices } from '@/hooks/invoices/useInvoices';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useBusinessUnitLabel } from '@/components/organisms/business-units/useBusinessUnitLabel';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { invoiceRowClearingLine } from '@/components/organisms/invoices/support/invoice-clearing';
import { InvoiceActionsMenu } from '@/components/organisms/invoices/InvoiceActionsMenu';
import { InvoiceStatusBadge } from '@/components/organisms/invoices/InvoiceStatusBadge';
import { invoiceStatusTone, statusToneDotClass } from '@/components/molecules/StatusBadgeTone';
import { EntityListCompactItem, EntitySplitListPanel } from '@/components/organisms';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import {
  DataTableDateRangeField,
  DataTableNumberFilterField,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  DataTableTextFilterField,
  EntityListActiveDatePill,
  EntityListAdvancedFiltersSheet,
  EntityListCountFooter,
  EntityRowActions,
  type DataTableNumberOperator,
} from '@/components/molecules';
import { computeInvoiceBadgeStatus, splitInvoiceStatusFilter } from '@/lib/invoice-status';
import type { Invoice, InvoiceStatus } from '@/types/invoice';

function fmt(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export type InvoiceStatusFilter = InvoiceStatus | 'all' | 'clearing' | 'unpaid';
type InvoiceSortField = 'createdAt' | 'date' | 'dueDate' | 'total';
type SortOption = {
  label: string;
  sortBy: InvoiceSortField;
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

// eslint-disable-next-line react-refresh/only-export-components
export const INVOICE_STATUS_FILTERS: { value: InvoiceStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'partial', label: 'Partial' },
  { value: 'clearing', label: 'Clearing' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
  { value: 'written_off', label: 'Written Off' },
];

const SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Invoice Date', sortBy: 'date', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Invoice Date', sortBy: 'date', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Due Date', sortBy: 'dueDate', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Due Date', sortBy: 'dueDate', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Total', sortBy: 'total', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Total', sortBy: 'total', sortDir: 'asc', icon: <ArrowUp size={12} /> },
];

export interface InvoiceListFilters {
  q: string;
  status: InvoiceStatusFilter;
  // NOT-542: business-unit filter ('all' = no filter). Optional so detail-route
  // search literals that don't carry a list filter still satisfy the type.
  businessUnitId?: string;
  invoiceNumber: string;
  totalOp: DataTableNumberOperator;
  totalValue: string;
  balanceOp: DataTableNumberOperator;
  balanceValue: string;
  dueFrom: string | undefined;
  dueTo: string | undefined;
  customerEmail: string;
  customerPhone: string;
  sort: number;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  page?: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_INVOICE_LIST_FILTERS: InvoiceListFilters = {
  q: '',
  status: 'all',
  businessUnitId: 'all',
  invoiceNumber: '',
  totalOp: 'eq',
  totalValue: '',
  balanceOp: 'eq',
  balanceValue: '',
  dueFrom: undefined,
  dueTo: undefined,
  customerEmail: '',
  customerPhone: '',
  sort: 0,
  dateFrom: undefined,
  dateTo: undefined,
  page: 1,
};

// eslint-disable-next-line react-refresh/only-export-components
export const INVOICE_STATUS_DOT: Record<string, string> = {
  draft: 'bg-gray-400',
  // Unpaid returns the invoices whose badge reads Open, so it takes that
  // badge's amber-brown dot — its own among these, and never Sent's blue.
  unpaid: statusToneDotClass(invoiceStatusTone('unpaid')),
  sent: 'bg-blue-500',
  viewed: 'bg-sky-500',
  partial: 'bg-yellow-500',
  paid: 'bg-green-500',
  overdue: 'bg-red-500',
  clearing: 'bg-amber-500',
  void: 'bg-gray-300',
  written_off: 'bg-gray-300',
};

function getNumberFilterParams(
  prefix: 'total' | 'balance',
  op: DataTableNumberOperator,
  raw: string,
) {
  if (!raw.trim()) return {};
  const expected = Number(raw);
  if (!Number.isFinite(expected)) return {};
  if (op === 'eq') return { [`${prefix}Eq`]: expected };
  if (op === 'gt' || op === 'gte') return { [`${prefix}Min`]: expected };
  return { [`${prefix}Max`]: expected };
}

interface InvoiceListPanelProps {
  selectedId?: string;
  onSelect: (invoice: Invoice) => void;
  /** Close the open invoice: a filter change left nothing to show in its place. */
  onClearSelection?: () => void;
  compact?: boolean;
  filters: InvoiceListFilters;
  onFiltersChange: (patch: Partial<InvoiceListFilters>) => void;
}

export function InvoiceListPanel({
  selectedId,
  onSelect,
  onClearSelection,
  compact = false,
  filters,
  onFiltersChange,
}: InvoiceListPanelProps) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const totalHidden = pricingHidden;
  // The server masks an invoice's balance, and drops the balance filter, under
  // PRICING and PAYMENT_INFO only: a CUSTOMER_FINANCIALS role (sales rep) can
  // read and filter by the balance of the invoices it owns (#2999).
  const balanceHidden = pricingHidden || paymentInfoHidden;
  // The clearing figures are part of the balance: shown wherever it is. The
  // Clearing badge and filter are for everyone — status is never withheld.
  const clearingVisible = !balanceHidden;
  const status = filters.status;
  const { pathname } = useLocation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const updateFilters = (patch: Partial<InvoiceListFilters>) => {
    onFiltersChange({ ...patch, page: patch.page ?? 1 });
  };
  // NOT-542: only offer the Business Unit filter to tenants that use them.
  const { data: businessUnits = [] } = useBusinessUnits('active');
  const hasBusinessUnits = businessUnits.length > 0;
  const { labelFor: businessUnitLabelFor } = useBusinessUnitLabel();
  const {
    searchInput: search,
    setSearchInput: setSearch,
    debouncedSearch,
  } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => updateFilters({ q: value }),
  });
  const availableSortOptions = totalHidden
    ? SORT_OPTIONS.filter((option) => option.sortBy !== 'total')
    : SORT_OPTIONS;
  const requestedSort = SORT_OPTIONS[filters.sort] ?? SORT_OPTIONS[0];
  const activeSort = availableSortOptions.includes(requestedSort)
    ? requestedSort
    : availableSortOptions[0];
  const activeSortIndex = availableSortOptions.indexOf(activeSort);
  const currentPage = filters.page ?? DEFAULT_INVOICE_LIST_FILTERS.page ?? 1;

  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
          to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
        }
      : undefined;
  const dueRange: DateRange | undefined =
    filters.dueFrom || filters.dueTo
      ? {
          from: filters.dueFrom ? parseISO(filters.dueFrom) : undefined,
          to: filters.dueTo ? parseISO(filters.dueTo) : undefined,
        }
      : undefined;

  // Everything that decides WHICH invoices match. Page and sort only reorder
  // or window them, so they are not a filter change (see the reselect below).
  const matchParams = {
    search: debouncedSearch || undefined,
    ...splitInvoiceStatusFilter(status),
    businessUnitId:
      hasBusinessUnits && filters.businessUnitId !== 'all' ? filters.businessUnitId : undefined,
    invoiceNumber: filters.invoiceNumber.trim() || undefined,
    customerEmail: filters.customerEmail.trim() || undefined,
    customerPhone: filters.customerPhone.trim() || undefined,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    ...(!totalHidden ? getNumberFilterParams('total', filters.totalOp, filters.totalValue) : {}),
    ...(!balanceHidden
      ? getNumberFilterParams('balance', filters.balanceOp, filters.balanceValue)
      : {}),
  };
  const query = useInvoices({
    page: currentPage,
    limit: 20,
    ...matchParams,
    sortBy: activeSort.sortBy,
    sortDir: activeSort.sortDir,
  });

  const invoices = query.data?.invoices ?? [];
  const totalCount = query.data?.total ?? 0;
  const totalPages = query.data?.totalPages ?? Math.max(1, Math.ceil(totalCount / 20));
  const firstInvoice = invoices[0];

  useEffect(() => {
    if (
      compact &&
      !selectedId &&
      firstInvoice &&
      pathname === '/dashboard/invoices' &&
      window.matchMedia('(min-width: 768px)').matches
    ) {
      onSelect(firstInvoice);
    }
  }, [compact, selectedId, firstInvoice, onSelect, pathname]);

  // A filter change can leave the open invoice out of the results: a Draft stays
  // on the right under "Clearing". Once the new results are in, open the first
  // match instead, or close the detail when nothing matches. Once per change,
  // and only while the open invoice is the one that was open when the filters
  // changed: an invoice the user opens afterwards is theirs, even if the page
  // no longer lists it.
  const matchKey = JSON.stringify(matchParams);
  const seenMatchKey = useRef(matchKey);
  const reselect = useRef<{ from: string | undefined } | null>(null);
  // No placeholder data: results present are the current filters' results.
  const loadedInvoices = query.data?.invoices;
  useEffect(() => {
    if (seenMatchKey.current !== matchKey) {
      seenMatchKey.current = matchKey;
      reselect.current = { from: selectedId };
    }
    const pending = reselect.current;
    if (!pending) return;
    if (!compact || !selectedId || pending.from !== selectedId) {
      reselect.current = null;
      return;
    }
    if (!loadedInvoices) return;
    reselect.current = null;
    if (loadedInvoices.some((invoice) => invoice.id === selectedId)) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    const first = loadedInvoices[0];
    if (first) onSelect(first);
    else onClearSelection?.();
  }, [matchKey, selectedId, compact, loadedInvoices, onSelect, onClearSelection]);

  function clearDate() {
    updateFilters({ dateFrom: undefined, dateTo: undefined });
  }

  const activeAdvancedCount =
    (filters.q.trim() ? 1 : 0) +
    (status !== 'all' ? 1 : 0) +
    (hasBusinessUnits && filters.businessUnitId !== 'all' ? 1 : 0) +
    (filters.invoiceNumber.trim() ? 1 : 0) +
    (!totalHidden && filters.totalValue.trim() ? 1 : 0) +
    (!balanceHidden && filters.balanceValue.trim() ? 1 : 0) +
    (dateRange?.from || dateRange?.to ? 1 : 0) +
    (dueRange?.from || dueRange?.to ? 1 : 0) +
    (filters.customerEmail.trim() ? 1 : 0) +
    (filters.customerPhone.trim() ? 1 : 0);

  function resetAdvancedFilters() {
    updateFilters({
      q: '',
      status: 'all',
      businessUnitId: 'all',
      invoiceNumber: '',
      totalOp: 'eq',
      totalValue: '',
      balanceOp: 'eq',
      balanceValue: '',
      dateFrom: undefined,
      dateTo: undefined,
      dueFrom: undefined,
      dueTo: undefined,
      customerEmail: '',
      customerPhone: '',
    });
  }

  return (
    <EntitySplitListPanel
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search customer or #...',
      }}
      statusFilter={{
        value: status,
        options: INVOICE_STATUS_FILTERS,
        dotColors: INVOICE_STATUS_DOT,
        onChange: (value) => updateFilters({ status: value as InvoiceStatusFilter }),
      }}
      sort={{
        activeIndex: activeSortIndex,
        activeLabel: activeSort.label,
        options: availableSortOptions,
        onSelect: (value) =>
          updateFilters({
            sort: SORT_OPTIONS.indexOf(availableSortOptions[value] ?? availableSortOptions[0]),
          }),
        onReset: () => updateFilters({ sort: SORT_OPTIONS.indexOf(availableSortOptions[0]) }),
      }}
      toolbarEnd={
        <EntityListAdvancedFiltersSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          activeCount={activeAdvancedCount}
          onReset={resetAdvancedFilters}
          resetDisabled={activeAdvancedCount === 0}
        >
          <DataTableFilterSection
            label="Invoice Number"
            active={!!filters.invoiceNumber.trim()}
            onClear={() => updateFilters({ invoiceNumber: '' })}
          >
            <DataTableSearchFilterField
              value={filters.invoiceNumber}
              onValueChange={(value) => updateFilters({ invoiceNumber: value })}
              placeholder="e.g. 1042"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Customer Name"
            active={!!filters.q.trim()}
            onClear={() => updateFilters({ q: '' })}
          >
            <DataTableSearchFilterField
              value={filters.q}
              onValueChange={(value) => updateFilters({ q: value })}
              placeholder="Search by name..."
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Status"
            active={status !== 'all'}
            onClear={() => updateFilters({ status: 'all' })}
          >
            <DataTableSelectFilterField
              value={status}
              onValueChange={(value) => updateFilters({ status: value as InvoiceStatusFilter })}
              placeholder="All statuses"
              options={INVOICE_STATUS_FILTERS.map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All statuses' : filter.label,
              }))}
            />
          </DataTableFilterSection>

          {hasBusinessUnits ? (
            <DataTableFilterSection
              label="Business Unit"
              active={filters.businessUnitId !== 'all'}
              onClear={() => updateFilters({ businessUnitId: 'all' })}
            >
              <DataTableSelectFilterField
                value={filters.businessUnitId ?? 'all'}
                onValueChange={(value) => updateFilters({ businessUnitId: value })}
                placeholder="All business units"
                options={[
                  { value: 'all', label: 'All business units' },
                  ...businessUnits.map((unit) => ({ value: unit.id, label: unit.name })),
                ]}
              />
            </DataTableFilterSection>
          ) : null}

          {!totalHidden ? (
            <DataTableFilterSection
              label="Total"
              active={!!filters.totalValue.trim()}
              onClear={() => updateFilters({ totalOp: 'eq', totalValue: '' })}
            >
              <DataTableNumberFilterField
                op={filters.totalOp}
                value={filters.totalValue}
                onOpChange={(op) => updateFilters({ totalOp: op })}
                onValueChange={(value) => updateFilters({ totalValue: value })}
              />
            </DataTableFilterSection>
          ) : null}

          {!balanceHidden ? (
            <DataTableFilterSection
              label="Balance Due"
              active={!!filters.balanceValue.trim()}
              onClear={() => updateFilters({ balanceOp: 'eq', balanceValue: '' })}
            >
              <DataTableNumberFilterField
                op={filters.balanceOp}
                value={filters.balanceValue}
                onOpChange={(op) => updateFilters({ balanceOp: op })}
                onValueChange={(value) => updateFilters({ balanceValue: value })}
              />
            </DataTableFilterSection>
          ) : null}

          <DataTableFilterSection
            label="Invoice Date"
            active={!!(dateRange?.from || dateRange?.to)}
            onClear={clearDate}
          >
            <DataTableDateRangeField
              range={dateRange}
              onSelect={(range) =>
                updateFilters({
                  dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Due Date"
            active={!!(dueRange?.from || dueRange?.to)}
            onClear={() => updateFilters({ dueFrom: undefined, dueTo: undefined })}
          >
            <DataTableDateRangeField
              range={dueRange}
              onSelect={(range) =>
                updateFilters({
                  dueFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  dueTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Customer Email"
            active={!!filters.customerEmail.trim()}
            onClear={() => updateFilters({ customerEmail: '' })}
          >
            <DataTableTextFilterField
              value={filters.customerEmail}
              onValueChange={(value) => updateFilters({ customerEmail: value })}
              placeholder="email@example.com"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Customer Phone"
            active={!!filters.customerPhone.trim()}
            onClear={() => updateFilters({ customerPhone: '' })}
          >
            <DataTableTextFilterField
              value={filters.customerPhone}
              onValueChange={(value) => updateFilters({ customerPhone: value })}
              placeholder="Phone number..."
            />
          </DataTableFilterSection>
        </EntityListAdvancedFiltersSheet>
      }
      belowToolbar={<EntityListActiveDatePill range={dateRange} onClear={clearDate} />}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      emptyIcon={FileText}
      emptyTitle="No invoices found"
      hasItems={invoices.length > 0}
      pagination={
        !query.isLoading && totalCount > 20
          ? {
              page: currentPage,
              totalPages,
              total: totalCount,
              onPrevious: () => onFiltersChange({ page: currentPage - 1 }),
              onNext: () => onFiltersChange({ page: currentPage + 1 }),
            }
          : undefined
      }
      footer={
        !query.isLoading && totalCount <= 20 ? (
          <EntityListCountFooter count={totalCount} singular="invoice" />
        ) : null
      }
    >
      {invoices.map((invoice) => {
        const status = computeInvoiceBadgeStatus(invoice);
        const contactName = invoice.contact
          ? `${invoice.contact.firstName}${invoice.contact.lastName ? ' ' + invoice.contact.lastName : ''}`
          : 'Unknown Customer';
        // NOT-542 — job type and business unit share the third line so the
        // unit is visible from the list without opening the invoice.
        const contextLine = [
          invoice.jobType?.name ?? 'No job type',
          businessUnitLabelFor(invoice.businessUnitId),
        ]
          .filter(Boolean)
          .join(' · ');
        const clearing = clearingVisible ? invoiceRowClearingLine(invoice, fmt) : null;

        return (
          <EntityListCompactItem
            key={invoice.id}
            selected={selectedId === invoice.id}
            onClick={() => onSelect(invoice)}
            content={
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{contactName}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  {invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : '—'}
                </p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{contextLine}</p>
              </div>
            }
            actions={
              <>
                <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                  <div className="flex justify-end">
                    <InvoiceStatusBadge status={status} />
                  </div>
                  {clearing ? (
                    <p className="text-xs text-muted-foreground" data-invoice-clearing>
                      {clearing}
                    </p>
                  ) : null}
                  <MaskedValue mask={['PRICING', 'CUSTOMER_FINANCIALS']}>
                    <p className="text-sm font-semibold">{fmt(invoice.total)}</p>
                  </MaskedValue>
                </div>
                <EntityRowActions>
                  <InvoiceActionsMenu invoice={invoice} />
                </EntityRowActions>
              </>
            }
          />
        );
      })}
    </EntitySplitListPanel>
  );
}
